import axios from "axios";
import BASE_URL from "../../config";

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

export const getUsers = async (filter = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/AppUser/get`,
      filter,
      {
        headers: getAuthHeader(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw {
      success: false,
      message: error.response?.data?.message || "Failed to fetch users",
      error
    };
  }
};

export const saveUser = async (user) => {
  try {
    // Format the data to match the API's expected format
    const formattedUser = {
      User_Id: user.user_Id,
      User_UserName: user.user_UserName,
      User_Active: user.user_Active,
      User_IsTempPwd: user.user_IsTempPwd,
      User_Branch_Id: user.user_Branch_Id,
      User_EmployeeId: user.user_EmployeeId,
      User_IsAdmin: user.user_IsAdmin,
      User_Company_Id: user.user_Company_Id,
      User_Role_Id: user.user_Role_Id
    };

    // For new users, password is required
    if (!user.user_Id) {
      if (!user.user_Password || !user.user_Password.trim()) {
        throw {
          success: false,
          message: "Password is required for new users",
          error: new Error("Password is required")
        };
      }
      formattedUser.User_Password = user.user_Password;
    } 
    // For existing users, only include password if it's provided
    else if (user.user_Password && user.user_Password.trim()) {
      formattedUser.User_Password = user.user_Password;
    } 
    // If editing and no password provided, send a special flag
    else {
      formattedUser.User_Password = "KEEP_EXISTING_PASSWORD";
    }

    // First save the user
    const saveResponse = await axios.post(
      `${BASE_URL}/api/AppUser/save`,
      formattedUser,
      {
        headers: getAuthHeader(),
      }
    );
    
    // After successful save, fetch all users to get the ID
    const allUsers = await getUsers();
    
    // Find the newly created/updated user by username
    const savedUser = allUsers.find(u => u.user_UserName === user.user_UserName);
    
    if (!savedUser) {
      console.warn('User saved but could not find user details:', user.user_UserName);
    }

    return {
      success: true,
      message: saveResponse.data.message || "User saved successfully",
      data: savedUser || {} // Return the full user object if found
    };
  } catch (error) {
    console.error("Error saving user:", error);
    throw {
      success: false,
      message: error.response?.data?.message || "Failed to save user",
      error
    };
  }
};

// Check if username exists
export const checkUsernameExists = async (username, excludeUserId = null) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/AppUser/check-username`,
      {
        Username: username,
        ExcludeUserId: excludeUserId
      },
      {
        headers: getAuthHeader(),
      }
    );
    return response.data.exists;
  } catch (error) {
    console.error("Error checking username:", error);
    throw {
      success: false,
      message: error.response?.data?.message || "Failed to check username",
      error
    };
  }
};

// Get users with branch and employee details resolved
export const getUsersWithDetails = async (filter = {}) => {
  try {
    const headers = getAuthHeader();
    const [users, branches, employees] = await Promise.all([
      getUsers(filter),
      axios.post(`${BASE_URL}/api/Branch/get`, {}, { headers }).then(res => res.data),
      axios.post(`${BASE_URL}/api/Employee/get`, {}, { headers }).then(res => res.data)
    ]);

    // Map the users data with reference data
    const enrichedUsers = users.map(user => ({
      ...user,
      branch_name: branches.find(b => b.branch_Id === user.User_Branch_Id)?.branch_NameEn || '',
      employee_name: employees.find(e => e.employee_Id === user.User_EmployeeId)?.employee_NameEn || ''
    }));

    return enrichedUsers;
  } catch (error) {
    console.error("Error fetching users with details:", error);
    throw error;
  }
}; 